import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import TiltCard from "../common/TiltCard";
import { getNewsVisual, formatNewsDate } from "./newsVisuals";

/** News card linking to the on-site article page. */
export default function NewsCard({ item }) {
  const { t, i18n } = useTranslation();
  const visual = getNewsVisual(item);
  const date = formatNewsDate(item.newsDate, i18n.language);

  return (
    <TiltCard max={6}>
      <Card
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-6px)",
            boxShadow: "0 32px 64px -32px rgba(13,18,40,0.6)",
            borderColor: "primary.main",
          },
        }}
      >
        <CardActionArea
          component={RouterLink}
          to={`/news/${item.id}`}
          sx={{ height: "100%" }}
        >
          <Box
            sx={{
              position: "relative",
              height: 132,
              background: visual.gradient,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                opacity: 0.25,
                backgroundImage:
                  "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.4) 0, transparent 35%)",
              }}
            />
            <ArticleRoundedIcon
              sx={{ position: "absolute", right: 16, bottom: 12, fontSize: 64, color: "rgba(255,255,255,0.25)" }}
            />
            {item.appKey && (
              <Chip
                label={item.appKey}
                size="small"
                sx={{
                  position: "absolute",
                  top: 14,
                  left: 14,
                  fontWeight: 700,
                  color: "#fff",
                  bgcolor: "rgba(7,11,24,0.35)",
                  backdropFilter: "blur(6px)",
                  border: "1px solid rgba(255,255,255,0.25)",
                }}
              />
            )}
          </Box>

          <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
            {date && (
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                <CalendarTodayRoundedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  {date}
                </Typography>
              </Stack>
            )}
            <Typography variant="h6" sx={{ mb: 1, lineHeight: 1.25 }}>
              {item.title}
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                mb: 2,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.body}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ mt: "auto", color: "primary.main", fontWeight: 700 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {t("newsReadMore")}
              </Typography>
              <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </TiltCard>
  );
}
